import type { FormCheckProps } from 'react-bootstrap'
import { Form } from 'react-bootstrap'
import React from 'react'
import isNil from 'lodash/isNil'
import { generateId } from '@ztechapps/common.utils'
import type { FieldEditorInjectedProps } from '../FieldEditorInjectedProps'
import type { InlineInputInjectedProps } from '../InlineInputInjectedProps'

export type OnChangeFunction = (value: boolean) => void
export type CheckboxInputProps = Omit<FormCheckProps, 'onChange' | 'form'> &
  FieldEditorInjectedProps &
  InlineInputInjectedProps & {
    name?: string
    submitting?: boolean
    index?: number
    onChange?: OnChangeFunction
    // Unlike NumberInput/TextAreaInput, this leaf never wires inputRef to ref — it's declared
    // only so it can be discarded before it leaks onto <Form.Check> as a DOM attribute.
    inputRef?: unknown
  }

export const CheckboxInput = ({
  disabled,
  submitting,
  name,
  onChange,
  value,
  checked,
  form: _form,
  meta: _meta,
  isDisabled: _isDisabled,
  noLabel: _noLabel,
  saveNow: _saveNow,
  cancelNow: _cancelNow,
  setNewValue: _setNewValue,
  documentId: _documentId,
  initialValues: _initialValues,
  validate: _validate,
  canEdit: _canEdit,
  editable: _editable,
  inputProps: _inputProps,
  inputRef: _inputRef,
  ...rest
}: CheckboxInputProps) => {
  const idRef = React.useRef(generateId(3))
  const id = `name__${name ?? ''}_${idRef.current}`

  const handleChange = (e) => {
    const changeValue = e.target.checked
    if (!onChange) return //if no onchange specify then just skip it
    onChange(changeValue)
  }

  return (
    <Form.Check
      {...{
        ...rest,
        name,
        value,
        disabled: disabled || submitting,
        type: 'checkbox',
        id,
        // Three consumer cases: explicit `checked` wins even when `false`; `value`-only derives a boolean;
        // neither prop supplied stays `undefined` so the input remains uncontrolled.
        checked: checked ?? (isNil(value) ? undefined : !!value),
        onChange: handleChange,
      }}
    />
  )
}
